import { transform } from '@/helpers/utils';
import { DrawingOperation, EditTextOperation, FormFieldOperation, ImageOperation, ShapeOperation, processPdf } from '@tailored-pdf/api';
import * as pdfjs from 'pdfjs-dist';
import { nanoid } from 'nanoid';
import {
  Annotation,
  EditableTextItem,
  EditingContext,
  FormField,
  FormTypes,
  ImageItem,
  PageMetadata,
  PDFJSTextItem,
} from '@/types';
import { PageShapes, Shape } from '@/types/shapes';
import { normalizeRgbColor, rgbArrayToHex, rgbToHex } from '@/helpers/colors';
import { TextRenderingMode } from '@/types/pdfJsTypes';
import {
  annotationPathsToPdfPoints,
  canvasCoordinatesToPdfCoordinates,
  normalizeRotation,
  pdfCoordinatesToCanvasCoordinates,
  rotateItem,
} from '@/helpers';
import { useEditorStore } from '@/components/PdfEditor/context/editorStore';

/* ------------------------------------------------------------------------------------------------- */
/* ------------------------------------------- PDF Export ------------------------------------------ */
/* ------------------------------------------------------------------------------------------------- */
const formFieldToOperation = (
  field: FormField,
  pageMetadata: PageMetadata,
  pageNumber: number = 0,
  zIndexMap: Record<string, number>,
) => {
  const pdfCoordinates = { ...field.pdfCoordinates };

  switch (pageMetadata.page.rotate) {
    case 90:
      pdfCoordinates.y += pdfCoordinates.height;
      break;
    case 180:
      pdfCoordinates.x -= pdfCoordinates.width;
      pdfCoordinates.y += pdfCoordinates.height;
      break;
    case 270:
      pdfCoordinates.x -= pdfCoordinates.width;
      break;
  }

  const boundingBox = {
    left: pdfCoordinates.x,
    bottom: pdfCoordinates.y - pdfCoordinates.height,
    top: pdfCoordinates.y,
    right: pdfCoordinates.x + pdfCoordinates.width,
  };

  /*if (pageMetadata.originalPageNumber !== -1 && pageMetadata.page.rotate !== 0) {
    const { width, height } = pageMetadata.page.getViewport({ scale: 1 });
    boundingBox = rotateBoundingBox(boundingBox, pageMetadata.page.rotate, width, height);
  }*/

  return {
    align: field.textAlign,
    name: field.name,
    page: pageNumber,
    multiline: field.type === FormTypes.TEXTAREA ? true : false,
    kind: field.type,
    value: field.value,
    boundingBox,
    color: field.color,
    borderColor: field.borderColor,
    fontSize: field.pdfCoordinates.fontSize,
    layerNumber: zIndexMap[field.id] ?? 0,
  } as FormFieldOperation;
};

const textItemToOperation = (
  textItem: EditableTextItem,
  pageMetadata: PageMetadata,
  pageNumber: number,
  zIndexMap: Record<string, number>,
  whiteBackground: boolean,
) => {
  const item = { ...textItem };

  if (pageMetadata.originalPageNumber !== -1 && pageMetadata.page.rotate !== 0) {
    item.angle = normalizeRotation(item.angle - pageMetadata.page.rotate);
  }

  // Embedded items save with the document's own font: the operation sends the
  // PostScript name (the backend's extracted font map is keyed by it, not by
  // pdf.js's internal name) plus the matched catalog font as the middle
  // fallback tier — the server draws embedded → matched → default per glyph,
  // mirroring the editor's CSS font-family stack.
  const embeddedEntry = item.isEmbedded ? useEditorStore.getState().documentFonts[item.fontName] : undefined;

  return {
    originalText: item.isDuplicated ? null : item.originalText ?? '',
    font: item.isEmbedded ? item.psFontName ?? embeddedEntry?.psName ?? item.fontName : item.fontName,
    ...(embeddedEntry?.matchedFontId ? { fallbackFontId: embeddedEntry.matchedFontId } : {}),
    text: item.text,
    position: {
      x: item.currentPdfCoordinates.x,
      y: item.currentPdfCoordinates.y,
    },
    area: item.originalPdfCoordinates,
    angleInDegrees: item.angle,
    fontSize: item.currentPdfCoordinates.fontSize,
    color: item.fillColor ?? '#000000',
    page: pageNumber,
    bold: item.isBold === true,
    italic: item.isItalic === true,
    embedded: item.isEmbedded === true,
    lineHeight: 1,
    layerNumber: zIndexMap[item.id] ?? 0,
    ...(whiteBackground ? { backgroundColor: '#FFFFFF' } : {}),
    ...(item.renderingMode === TextRenderingMode.INVISIBLE ? { renderingMode: TextRenderingMode.INVISIBLE } : {}),
  } as EditTextOperation;
};

const shapeTOperation = (
  shape: Shape,
  pageMetadata: PageMetadata,
  pageNumber: number = 0,
  zIndexMap: Record<string, number>,
) => {
  let shapeItem = shape;
  if (pageMetadata.originalPageNumber !== -1 && pageMetadata.page.rotate !== 0) {
    const { width, height } = pageMetadata.page.getViewport({ scale: 1 });
    shapeItem = rotateItem(shape, pageMetadata.page.rotate, width, height);
  }

  return {
    shape: shapeItem.type.toString(),
    points: shapeItem.pdfPoints,
    page: pageNumber,
    backgroundColor: normalizeRgbColor(shapeItem.backgroundColor),
    borderColor: normalizeRgbColor(shapeItem.borderColor),
    borderWidth: shapeItem.borderWidth ?? 1,
    layerNumber: zIndexMap[shapeItem.id] ?? 0,
  } as ShapeOperation;
};

const annotationToDrawingOperation = (
  annotation: Annotation,
  pageMetadata: PageMetadata,
  pageNumber: number,
  zIndexMap: Record<string, number>,
): DrawingOperation => {
  const strokes = annotationPathsToPdfPoints(
    annotation.paths,
    { x: annotation.currentCanvasCoordinates.x, y: annotation.currentCanvasCoordinates.y },
    pageMetadata.page,
    annotation.scale,
  );

  return {
    strokes,
    color: annotation.color,
    lineWidth: annotation.lineWidth ?? 1,
    highlight: annotation.type === 'HIGHLIGHT',
    page: pageNumber,
    layerNumber: zIndexMap[annotation.id] ?? 0,
  };
};

const imageToOperation = (
  item: ImageItem,
  pageMetadata: PageMetadata,
  pageNumber: number,
  zIndexMap: Record<string, number>,
) => {
  const imageItem = { ...item };
  const pdfCoordinates = { ...imageItem.currentPdfCoordinates };

  switch (pageMetadata.page.rotate) {
    case 90:
      pdfCoordinates.x -= pdfCoordinates.width;
      break;
    case 180:
      pdfCoordinates.x -= pdfCoordinates.width;
      pdfCoordinates.y -= pdfCoordinates.height;
      break;
    case 270:
      pdfCoordinates.y -= pdfCoordinates.height;
      break;
  }
  /*if (pageMetadata.originalPageNumber !== -1 && pageMetadata.page.rotate !== 0) {
    const { width, height } = pageMetadata.page.getViewport({ scale: 1 });
    imageItem = rotateItem(item, pageMetadata.page.rotate, width, height);
  }*/

  return {
    position: {
      x: pdfCoordinates.x,
      y: pdfCoordinates.y,
    },
    width: pdfCoordinates.width,
    height: pdfCoordinates.height,
    page: pageNumber,
    image: imageItem.fileId,
    rotation: (imageItem.rotation ?? 0) - pageMetadata.page.rotate,
    layerNumber: zIndexMap[imageItem.id] ?? 0,
  };
};

export const exportPDF = async (uploadedFileId: string, context: EditingContext, shapes: PageShapes) => {
  const editTextOperations: EditTextOperation[] = [];
  const imageOperations: ImageOperation[] = [];
  const drawingOperations: DrawingOperation[] = [];
  const shapeOperations: ShapeOperation[] = [];
  const formFieldOperations: FormFieldOperation[] = [];
  const { zIndexMap, dirtyTextItems } = context;

  Object.keys(context.pageTextItems).forEach((pageNumber) => {
    const pageNumberInt = parseInt(pageNumber);
    const flatDirtyTextItems = Object.values(dirtyTextItems).reduce((p, c) => {
      return { ...p, ...c };
    }, {});

    /* ------------------------- Text Edit Operations ------------------------- */
    context.pageTextItems[pageNumberInt].map(async (item) => {
      if (!item.isEdited) return;

      const whiteBackground = item.id in flatDirtyTextItems && flatDirtyTextItems[item.id].bgMode === 'WHITE';

      editTextOperations.push(
        textItemToOperation(item, context.pagesMetadata[pageNumberInt], pageNumberInt, zIndexMap, whiteBackground),
      );
    });

    /* ------------------------- Image Operations ------------------------- */
    (context.pageImageItems[pageNumber] ?? []).forEach((item) => {
      imageOperations.push(imageToOperation(item, context.pagesMetadata[pageNumberInt], pageNumberInt, zIndexMap));
    });

    /* ------------------------- Drawing Operations ------------------------- */
    (context.pageAnnotations[pageNumber] ?? []).forEach((annotation) => {
      if (annotation.paths.length === 0) return;
      drawingOperations.push(
        annotationToDrawingOperation(annotation, context.pagesMetadata[pageNumberInt], pageNumberInt, zIndexMap),
      );
    });
  });

  /* ------------------------- Shape Operations ------------------------- */

  Object.keys(shapes).forEach((pageNumber) => {
    const pageNumberInt = parseInt(pageNumber);

    shapes[pageNumberInt].forEach((shape: Shape) => {
      shapeOperations.push(shapeTOperation(shape, context.pagesMetadata[pageNumberInt], pageNumberInt, zIndexMap));
    });

    context.formFields[pageNumberInt].forEach((field) => {
      formFieldOperations.push(
        formFieldToOperation(field, context.pagesMetadata[pageNumberInt], pageNumberInt, zIndexMap),
      );
    });
  });

  /* TODO: make sure images, signatures and annotations are uploaded before processing */
  await processPdf(
    {
      drawingOperations,
      appendFormFieldOperations: formFieldOperations,
      appendLineOperations: [],
      appendLinkOperations: [],
      appendTextOperations: [],
      editTextOperations: editTextOperations,
      highlightTextOperations: [],
      imageOperations: imageOperations,
      shapeOperations: shapeOperations,
      addPageOperations: context.addedPages,
      deletePageOperations: context.deletedPages,
      ...(context.documentPassword ? { password: context.documentPassword } : {}),
    },
    { id: uploadedFileId },
  );
};

/* ------------------------------------------------------------------------------------------------- */
/* ----------------------------------- Per-page Text Extraction ------------------------------------ */
/* ------------------------------------------------------------------------------------------------- */

export interface ParsedPageText {
  page: pdfjs.PDFPageProxy;
  textItems: PDFJSTextItem[];
  fonts: Record<string, unknown>;
  // Raw pdfjs font objects keyed by font name (e.g. "g_d0_f1"). Callers
  // enrich these via describeEmbeddedFont (fontMatching.ts) before passing
  // them to PdfJSTextItemtoTextItem / groupTextItemsIntoLines. Kept raw here
  // to avoid a PdfService → pdfHelpers circular import.
  rawFontObjects: Record<string, unknown>;
  annotations: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export const getPageTextItems = async (pdfDoc: pdfjs.PDFDocumentProxy, pageIndex: number): Promise<ParsedPageText> => {
  const page = await pdfDoc.getPage(pageIndex + 1);
  const textContent = await page.getTextContent();
  // operatorList must be requested so commonObjs are populated before reads below.
  await page.getOperatorList();
  const annotations = await page.getAnnotations();

  const rawFontObjects: Record<string, unknown> = {};
  for (const fontName in textContent.styles) {
    rawFontObjects[fontName] = page.commonObjs.get(fontName);
  }

  return {
    page,
    textItems: textContent.items as unknown as PDFJSTextItem[],
    fonts: textContent.styles,
    rawFontObjects,
    annotations,
  };
};

export const parseAnnotations = (annotations: any[], page: pdfjs.PDFPageProxy, scale: number): FormField[] => {
  // eslint-disable-line @typescript-eslint/no-explicit-any
  const formFields: FormField[] = [];
  annotations.forEach((annotation) => {
    if (annotation.fieldType !== 'Tx') return;

    const rect = annotation.rect;
    const [x1, y1, x2, y2] = rect;

    const pdfWidth = x2 - x1;
    const pdfHeight = y2 - y1;

    const pdfFontSize = annotation.defaultAppearanceData.fontSize;
    const pdfCoordinates = {
      x: x1,
      y: y1 + pdfHeight,
      width: pdfWidth,
      height: pdfHeight,
      fontSize: pdfFontSize,
    };
    const fontColor = rgbArrayToHex(annotation.defaultAppearanceData.fontColor ?? [0, 0, 0]);

    const canvasCoordinates = pdfCoordinatesToCanvasCoordinates(pdfCoordinates, page, scale, true);

    const borderColor = annotation.borderColor
      ? rgbToHex({ r: annotation.borderColor[0], g: annotation.borderColor[1], b: annotation.borderColor[2] })
      : '#000000';

    formFields.push({
      id: nanoid(),
      type: annotation.multiLine ? FormTypes.TEXTAREA : FormTypes.TEXT,
      name: annotation.fieldName,
      value: annotation.fieldValue,
      canvasCoordinates,
      pdfCoordinates: canvasCoordinatesToPdfCoordinates(canvasCoordinates, page, scale),
      color: fontColor,
      borderColor: borderColor,
      textAlign: annotation.textAlignment === 0 ? 'left' : annotation.textAlignment === 1 ? 'center' : 'right',
      existing: true,
    });
  });

  return formFields;
};

export const calculateRenderedTextItemProps = (
  textItem: PDFJSTextItem,
  page: pdfjs.PDFPageProxy,
  styles: Record<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
  const { pageHeight, pageX, pageY }: any = page.getViewport({ scale: 1 }).rawDims; // eslint-disable-line @typescript-eslint/no-explicit-any
  const _transform = [1, 0, 0, -1, -pageX, pageY + pageHeight];
  const textDivProperties = {
    angle: 0,
    canvasWidth: 0,
    hasText: textItem.str !== '',
    hasEOL: textItem.hasEOL,
    fontSize: 0,
  };

  const tx = transform(_transform, textItem.transform);
  let angle = Math.atan2(tx[1], tx[0]);
  const style = styles[textItem.fontName];
  if (style?.vertical) {
    angle += Math.PI / 2;
  }

  const fontFamily = style?.fontFamily;
  const fontHeight = Math.hypot(tx[2], tx[3]);
  const fontAscent = 0; //fontHeight * getAscent(fontFamily);

  let left, top;
  if (angle === 0) {
    left = tx[4];
    top = tx[5] - fontAscent;
  } else {
    left = tx[4] + fontAscent * Math.sin(angle);
    top = tx[5] - fontAscent * Math.cos(angle);
  }

  const divStyle: { left?: number; top?: number; fontSize?: number; fontFamily?: string } = {};
  divStyle.left = left;
  divStyle.top = top;
  divStyle.fontSize = fontHeight;
  divStyle.fontFamily = fontFamily;

  textDivProperties.fontSize = fontHeight;

  // Keeps screen readers from pausing on every new text span.
  //textDiv.setAttribute("role", "presentation");

  // geom.dir may be 'ttb' for vertical texts.
  //textDiv.dir = textItem.dir;

  if (angle !== 0) {
    textDivProperties.angle = angle * (180 / Math.PI);
  }
  // We don't bother scaling single-char text divs, because it has very
  // little effect on text highlighting. This makes scrolling on docs with
  // lots of such divs a lot faster.
  let shouldScaleText = false;
  if (textItem.str.length > 1) {
    shouldScaleText = true;
  } else if (textItem.str !== ' ' && textItem.transform[0] !== textItem.transform[3]) {
    const absScaleX = Math.abs(textItem.transform[0]);
    const absScaleY = Math.abs(textItem.transform[3]);
    // When the horizontal/vertical scaling differs significantly, also scale
    // even single-char text to improve highlighting (fixes issue11713.pdf).
    if (absScaleX !== absScaleY && Math.max(absScaleX, absScaleY) / Math.min(absScaleX, absScaleY) > 1.5) {
      shouldScaleText = true;
    }
  }
  if (shouldScaleText) {
    textDivProperties.canvasWidth = style?.vertical ? textItem.height : textItem.width;
  }

  return { divStyle, textDivProperties };
};
